import React from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { AsyncActionIcon } from '~components';
import { faEnvelope } from '~components/icon';

import { GocardlessClientSignupRow } from '~direct-debits/models';
import { formatSentAt } from '~direct-debits/lib/formatSentAt';

import { useSendSignupLinkMutation } from '~direct-debits/actions/clientSignups';

interface ComponentProps {
  row: GocardlessClientSignupRow;
  isConnected?: boolean;
}

const buildTooltip = (row: GocardlessClientSignupRow, isConnected: boolean): string => {
  if (!isConnected) return 'GoCardless connection is not active';
  if (!row.email) return 'No email on file';
  if (row.last_sent_at) return `Resend signup link (last sent ${formatSentAt(row.last_sent_at)})`;
  return 'Send signup link';
};

const SendLinkButton: React.FC<ComponentProps> = ({ row, isConnected = true }) => {
  const [sendSignupLink] = useSendSignupLinkMutation();
  const { showToast } = useToast();

  if (row.status === 'active') {
    return (
      <span
        style={{ display: 'inline-block', width: '1.5rem', height: '1.5rem' }}
        aria-hidden="true"
      />
    );
  }

  const hasEmail = Boolean(row.email);
  const isDisabled = !isConnected || !hasEmail;

  const handleClick = async () => {
    try {
      await sendSignupLink(row.client_id).unwrap();
      showToast('success', `Signup link sent to ${row.full_name || 'client'}`);
    } catch (err) {
      showToast('danger', getErrorMessage(err));
    }
  };

  const reason = buildTooltip(row, isConnected);

  return (
    <AsyncActionIcon
      id={`dd-send-signup-link-${row.client_id}`}
      icon={faEnvelope}
      onClick={handleClick}
      disabled={isDisabled}
      toolTip={reason}
      ariaLabel={reason}
      successTitle="Signup link sent"
      errorTitle="Failed to send signup link"
    />
  );
};

export default SendLinkButton;
